'use client'

import { Plane, Calendar, CheckCircle2, AlertTriangle } from 'lucide-react'

interface BigglesToolResultProps {
  tool: string
  result: unknown
}

/*
 * Migrated to the semantic status tokens (2026-09-01, off the colour ratchet).
 * The fleet summary's status text carried no dark siblings at all — dim, muddy
 * greens and yellows on a dark phone, which is what "Biggles is not dark mode
 * friendly" looked like — and the chips re-implemented mode-awareness by hand.
 * The tokens carry both modes for free. (No literal class names in this
 * comment on purpose — the ratchet reads this file.)
 */

export function BigglesToolResult({ tool, result }: BigglesToolResultProps) {
  const data = result as Record<string, unknown>

  // Don't render error results as cards
  if (data.error) return null

  switch (tool) {
    case 'get_my_reservations':
      return <ReservationResults data={data} />
    case 'get_fleet_status':
      return <FleetResults data={data} />
    case 'get_my_curriculum_progress':
      return <ProgressResults data={data} />
    case 'create_reservation':
      return <PreviewResult data={data} />
    case 'confirm_action':
      return <ConfirmationResult data={data} />
    default:
      return null
  }
}

function ReservationResults({ data }: { data: Record<string, unknown> }) {
  const reservations = (data.reservations as Array<Record<string, unknown>>) || []
  if (reservations.length === 0) return null

  return (
    <div className="space-y-1.5 w-full">
      {reservations.slice(0, 5).map((r, i) => (
        <div
          key={i}
          className="flex items-start gap-2 rounded-md border bg-card p-2 text-xs"
        >
          <Calendar className="h-3.5 w-3.5 text-muted-foreground shrink-0 mt-0.5" />
          <div className="min-w-0">
            <div className="font-medium truncate">{r.activity_type as string}</div>
            <div className="text-muted-foreground">{r.start as string}</div>
            <div className="text-muted-foreground truncate">
              {r.aircraft as string}
              {r.cfi && r.cfi !== 'No CFI assigned' ? ` with ${r.cfi}` : ''}
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function FleetResults({ data }: { data: Record<string, unknown> }) {
  const fleet = (data.fleet as Array<Record<string, unknown>>) || []
  const summary = data.summary as Record<string, number> | undefined

  if (fleet.length === 0) return null

  return (
    <div className="rounded-md border bg-card p-2 text-xs w-full">
      <div className="flex items-center gap-1.5 font-medium mb-1.5">
        <Plane className="h-3.5 w-3.5" />
        Fleet Status
      </div>
      {summary && (
        <div className="flex gap-3 text-muted-foreground mb-1.5">
          <span className="text-success">{summary.active} active</span>
          {summary.in_maintenance > 0 && (
            <span className="text-warning">{summary.in_maintenance} maintenance</span>
          )}
          {summary.inactive > 0 && (
            <span className="text-destructive">{summary.inactive} inactive</span>
          )}
        </div>
      )}
      <div className="space-y-0.5">
        {fleet.slice(0, 8).map((ac, i) => (
          <div key={i} className="flex items-center justify-between">
            <span>{ac.tail_number as string}</span>
            <span
              className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                ac.status === 'active'
                  ? 'bg-success-muted text-success-muted-foreground'
                  : ac.status === 'maintenance'
                  ? 'bg-warning-muted text-warning-muted-foreground'
                  : 'bg-destructive-muted text-destructive-muted-foreground'
              }`}
            >
              {ac.status as string}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

function ProgressResults({ data }: { data: Record<string, unknown> }) {
  const assignments = (data.assignments as Array<Record<string, unknown>>) || []
  if (assignments.length === 0) return null

  return (
    <div className="space-y-1.5 w-full">
      {assignments.map((a, i) => {
        const progress = parseFloat(
          (a.overall_progress as string || '0').replace('%', '')
        )
        return (
          <div key={i} className="rounded-md border bg-card p-2 text-xs">
            <div className="font-medium mb-1">{a.curriculum as string}</div>
            <div className="w-full bg-muted rounded-full h-2 mb-1">
              <div
                className="bg-primary h-2 rounded-full transition-all"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>{a.overall_progress as string} complete</span>
              <span>{a.current_section as string}</span>
            </div>
          </div>
        )
      })}
    </div>
  )
}

function PreviewResult({ data }: { data: Record<string, unknown> }) {
  const preview = data.preview as Record<string, unknown> | undefined
  const conflicts = data.conflicts as Record<string, unknown> | null

  if (!preview) return null

  return (
    <div className="rounded-md border bg-card p-2 text-xs w-full">
      <div className="flex items-center gap-1.5 font-medium mb-1.5">
        <Calendar className="h-3.5 w-3.5" />
        Reservation Preview
      </div>
      <div className="space-y-0.5 text-muted-foreground">
        <div><span className="font-medium text-foreground">Date:</span> {preview.date as string}</div>
        <div><span className="font-medium text-foreground">Time:</span> {preview.time as string}</div>
        <div><span className="font-medium text-foreground">Type:</span> {preview.activity_type as string}</div>
        <div><span className="font-medium text-foreground">Aircraft:</span> {preview.aircraft as string}</div>
        <div><span className="font-medium text-foreground">CFI:</span> {preview.cfi as string}</div>
      </div>
      {conflicts && (
        <div className="flex items-center gap-1.5 mt-1.5 text-warning">
          <AlertTriangle className="h-3.5 w-3.5" />
          <span>Scheduling conflicts detected</span>
        </div>
      )}
    </div>
  )
}

function ConfirmationResult({ data }: { data: Record<string, unknown> }) {
  if (!data.success) return null
  const reservation = data.reservation as Record<string, unknown>

  return (
    <div className="rounded-md border border-success-muted-foreground/20 bg-success-muted p-2 text-xs w-full">
      <div className="flex items-center gap-1.5 font-medium text-success-muted-foreground mb-1">
        <CheckCircle2 className="h-3.5 w-3.5" />
        Reservation Booked!
      </div>
      <div className="space-y-0.5 text-muted-foreground">
        <div>{reservation?.start as string} - {reservation?.end as string}</div>
        <div>{reservation?.aircraft as string}</div>
        <div>{reservation?.activity_type as string}</div>
      </div>
    </div>
  )
}
